const PREFIX = 'tim-massage-admin-session';

export type AdminSurface = 'cms' | 'owner';

function key(surface: AdminSurface) {
  return `${PREFIX}:${surface}`;
}

export function readAdminToken(surface: AdminSurface): string {
  try {
    return window.sessionStorage.getItem(key(surface)) || '';
  } catch {
    return '';
  }
}

export function writeAdminToken(surface: AdminSurface, accessToken: string) {
  try {
    if (accessToken) window.sessionStorage.setItem(key(surface), accessToken);
    else window.sessionStorage.removeItem(key(surface));
  } catch {
    // Private browsing or an embedded WebView can deny storage. The in-memory
    // session remains usable for the current document in that case.
  }
}

export const browserSessionHeaders = { 'X-Tim-Session-Mode': 'cookie', 'X-Tim-Session-Scope': 'cms' } as const;
export const ownerBrowserSessionHeaders = { 'X-Tim-Session-Mode': 'cookie', 'X-Tim-Session-Scope': 'owner' } as const;

const refreshes: Partial<Record<AdminSurface, Promise<string>>> = {};

async function performAdminRefresh(surface: AdminSurface): Promise<string> {
  const response = await fetch('/api/v1/auth/refresh', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...(surface === 'owner' ? ownerBrowserSessionHeaders : browserSessionHeaders) },
    body: '{}',
  });
  const envelope = await response.json().catch(() => null);
  if (!response.ok || !envelope?.data?.accessToken) {
    writeAdminToken(surface, '');
    return '';
  }
  const accessToken = String(envelope.data.accessToken);
  writeAdminToken(surface, accessToken);
  return accessToken;
}

export function refreshAdminToken(surface: AdminSurface): Promise<string> {
  // Access tokens can expire while a CMS screen is loading many panels in
  // parallel. Refresh tokens rotate after one use, so concurrent refreshes
  // would invalidate each other and log the operator out. All callers on the
  // same surface must await one shared rotation.
  const pending = refreshes[surface];
  if (pending) return pending;
  const current = performAdminRefresh(surface).finally(() => {
    if (refreshes[surface] === current) delete refreshes[surface];
  });
  refreshes[surface] = current;
  return current;
}
